import React, { useState, useRef, useEffect } from "react";
import { createUser, manageUsersInGroup } from "../../services/userApi";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Info } from "lucide-react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CustomTextField } from "./CustomTextField.tsx";
import { CreateUserTabProps, PasswordValidations } from "./types";

interface StyledProps {
  $isDark?: boolean;
  $variant?: 'primary' | 'danger';
  disabled?: boolean;
}

const FormContainer = styled(Box)<StyledProps>`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledTypography = styled(Typography)<StyledProps>`
  color: ${props => props.$isDark ? "#FFFFFF" : "#1F2937"};
  margin-bottom: 8px;
  font-weight: 500;
`;

const RequiredStar = styled('span')`
  color: #FF3D3D;
`;

const PasswordCriteriaBox = styled(Box)<StyledProps>`
  background-color: ${props => props.$isDark ? '#27272A' : '#F3F4F6'};
  padding: 16px;
  border-radius: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  box-shadow: ${props => props.$isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
`;

const CriteriaItem = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const CriteriaText = styled(Typography)<StyledProps>`
  font-size: 14px;
  line-height: 20px;
  flex: 1;
  padding-right: 8px;
  color: ${props => props.$isDark ? "#FFFFFF" : "#1F2937"};
`;

const ActionButton = styled('button')<StyledProps>`
  padding: 8px 16px;
  background-color: ${props => {
    if (props.disabled) return props.$isDark ? "#1F1F1F" : "#D1D5DB";
    if (props.$variant === 'danger') return props.$isDark ? "#FF3D3D" : "#DC2626";
    return props.$isDark ? "#22C55E" : "#16A34A";
  }};
  color: ${props => props.disabled 
    ? (props.$isDark ? "#666666" : "#9CA3AF") 
    : "#FFFFFF"};
  border: none;
  border-radius: 8px;
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  font-weight: 500;
  min-width: 100px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => {
      if (props.disabled) return props.$isDark ? "#1F1F1F" : "#D1D5DB";
      if (props.$variant === 'danger') return props.$isDark ? "#FF5252" : "#B91C1C";
      return props.$isDark ? "#16A34A" : "#059669";
    }};
  }
`;

const InfoBox = styled(Box)<StyledProps>`
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid ${props => props.$isDark ? '#22C55E' : '#16A34A'};
  background-color: ${props => props.$isDark ? 'rgba(34, 197, 94, 0.1)' : 'rgba(22, 163, 74, 0.05)'};
`;

const InfoText = styled(Typography)<StyledProps>`
  color: ${props => props.$isDark ? '#A7F3D0' : '#166534'};
  font-size: 14px;
  line-height: 1.5;
  margin-left: 8px;
`;

const CreateUserTab: React.FC<CreateUserTabProps> = ({ 
  isDarkMode, 
  isLoading, 
  setIsLoading, 
  textConfig, 
  setAlertConfig, 
  onUserCreated 
}) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  const validatePassword = (password: string): PasswordValidations => {
    return {
      length: password.length >= 8,
      number: /\d/.test(password),
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>_]/.test(password),
    };
  };

  useEffect(() => {
    const criteria = validatePassword(password);
    setIsPasswordValid(Object.values(criteria).every(Boolean));
  }, [password]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleFocus = () => {
    setShowPasswordCriteria(true);
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (toggleButtonRef.current && toggleButtonRef.current.contains(event.relatedTarget as Node)) {
      return;
    }
    setShowPasswordCriteria(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!username.trim()) {
      setAlertConfig({
        open: true,
        type: "error",
        message: "El nombre de usuario es requerido",
        title: "Error de validación",
        onConfirm: () => setAlertConfig(prev => ({ ...prev, open: false })),
      });
      return;
    }
    
    if (!password.trim()) {
      setAlertConfig({
        open: true,
        type: "error",
        message: "La contraseña es requerida",
        title: "Error de validación",
        onConfirm: () => setAlertConfig(prev => ({ ...prev, open: false })),
      });
      return;
    }
    
    if (!isPasswordValid) {
      setAlertConfig({
        open: true,
        type: "error",
        message: "La contraseña no cumple con los criterios requeridos",
        title: "Error de validación",
        onConfirm: () => setAlertConfig(prev => ({ ...prev, open: false })),
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      setAlertConfig({
        open: true,
        type: "loading",
        message: textConfig.CREATION_USER_TOAST_USER_CREATING || "Creando usuario...",
        title: "Creando usuario",
        onConfirm: undefined,
      });
      
      const trimmedUsername = username.trim()
      const userData = {
        username: trimmedUsername,
        password: password,
      };
      
      await createUser(userData);
      await manageUsersInGroup([trimmedUsername], "user", "add")
      setAlertConfig({
        open: true,
        type: "success",
        message: textConfig.CREATION_USER_TOAST_USER_CREATED || "Usuario creado correctamente",
        title: "Éxito",
        onConfirm: () => {
          setAlertConfig(prev => ({ ...prev, open: false }));
          setUsername("");
          setPassword("");
          
          if (onUserCreated) {
            onUserCreated();
          }
        },
      });
      
    } catch (error) {
      console.error("Error al crear el usuario:", error);
      
      let errorMessage = textConfig.CREATION_USER_TOAST_USER_ERROR || "Error al crear usuario";
      
      if (error.message) {
        if (error.message.includes("409") || error.message.includes("ya existe")) {
          errorMessage = "El usuario ya existe";
        } else if (error.message.includes("400") || error.message.includes("inválidos")) {
          errorMessage = "Datos de usuario inválidos";
        } else {
          errorMessage = error.message;
        }
      }
      
      setAlertConfig({
        open: true,
        type: "error",
        message: errorMessage,
        title: "Error",
        onConfirm: () => setAlertConfig(prev => ({ ...prev, open: false })),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordCriteria = validatePassword(password);

  return (
    <FormContainer $isDark={isDarkMode} component="form" onSubmit={handleSubmit}>
      <InfoBox $isDark={isDarkMode}>
        <Info 
          size={24} 
          style={{ 
            color: isDarkMode ? '#22C55E' : '#16A34A',
            marginTop: '2px',
            flexShrink: 0 
          }} 
        />
        <InfoText $isDark={isDarkMode}>
          Aquí puedes crear nuevos usuarios del sistema. Solo se requiere nombre de usuario y contraseña. Cuando el usuario vaya a iniciar sesión este debe cambiar la contraseña.
        </InfoText>
      </InfoBox>

      <div>
        <StyledTypography $isDark={isDarkMode}>
          {textConfig.CREATION_USER_TEXT_USERNAME || "Nombre de usuario"} <RequiredStar>*</RequiredStar>
        </StyledTypography>
        <CustomTextField
          isFullWidth
          placeholder={textConfig.CREATION_USER_TEXT_USERNAME || "Nombre de usuario"}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={isSubmitting || isLoading}
        />
      </div>

      <div>
        <StyledTypography $isDark={isDarkMode}>
          {textConfig.CREATION_USER_TEXT_TEMP_PASSWORD || "Contraseña"} <RequiredStar>*</RequiredStar>
        </StyledTypography>
        <Box sx={{ position: 'relative' }}>
          <CustomTextField
            isFullWidth
            type={showPassword ? "text" : "password"}
            placeholder={textConfig.CREATION_USER_TEXT_TEMP_PASSWORD || "Contraseña"}
            value={password}
            onChange={handlePasswordChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            required
            disabled={isSubmitting || isLoading}
            inputRef={passwordInputRef}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            ref={toggleButtonRef}
            disabled={isSubmitting || isLoading}
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              padding: 8,
              cursor: (isSubmitting || isLoading) ? 'not-allowed' : 'pointer',
              color: isDarkMode ? '#FFFFFF' : '#1F2937',
              opacity: (isSubmitting || isLoading) ? 0.5 : 1,
            }}
          >
            {showPassword ? (
              <EyeSlashIcon style={{ height: 20, width: 20 }} />
            ) : (
              <EyeIcon style={{ height: 20, width: 20 }} />
            )}
          </button>
        </Box>

        {showPasswordCriteria && (
          <PasswordCriteriaBox $isDark={isDarkMode}>
            {[
              { key: 'length', text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA?.LENGTH || "Al menos 8 caracteres" },
              { key: 'number', text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA?.NUMBER || "Al menos un número" },
              { key: 'lowercase', text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA?.LOWERCASE || "Al menos una letra minúscula" },
              { key: 'uppercase', text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA?.UPPERCASE || "Al menos una letra mayúscula" },
              { key: 'special', text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA?.SPECIAL_CHAR || "Al menos un carácter especial" }
            ].map(({ key, text }) => (
              <CriteriaItem key={key}>
                <input
                  type="checkbox"
                  checked={passwordCriteria[key as keyof typeof passwordCriteria]}
                  readOnly
                  style={{ 
                    width: 16,
                    height: 16,
                    cursor: 'default',
                    accentColor: isDarkMode ? '#22C55E' : '#16A34A',
                    marginTop: 3
                  }}
                />
                <CriteriaText $isDark={isDarkMode}>{text}</CriteriaText>
              </CriteriaItem>
            ))}
          </PasswordCriteriaBox>
        )}
      </div>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
        <ActionButton
          type="submit"
          disabled={!isPasswordValid || !username.trim() || isSubmitting || isLoading}
          $isDark={isDarkMode}
        >
          {(isSubmitting || isLoading)
            ? "Creando..." 
            : (textConfig.CREATION_USER_TEXT_CREATE || "Crear usuario")
          }
        </ActionButton>
      </Box>
    </FormContainer>
  );
};

export default CreateUserTab;